import { useMemo, useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { useNavigate, useParams } from 'react-router-dom';
import { CountryIdentifiers, ROUTES } from 'core';
import { selectSelectedCountryId } from 'store/country/countrySlice';
import { useAppSelector } from 'store';
import { useGetCandidateExamDataQuery, useGetPaymentDetailsQuery } from 'store/payment/paymentAPI';

import { Divider, LoadingCard, MainContainer, Quote, ReadonlyField } from '@britishcouncil/react-solas-ors3';
import { Button } from '@britishcouncil/react-solas';

import { BangladeshWarningMsg } from './components/BangladeshWarningMsg';
import { ExamCard } from './components/ExamCard';
import { KuwaitWarningMsg } from './components/KuwaitWarningMsg';
import { PaymentError } from './components/PaymentError';
import { PaymentNotFound } from './components/PaymentNotFound';
import { UaeWarningMsg } from './components/UaeWarningMsg';
import { usePaymentSummaryData } from './usePaymentSummaryData';
import { getTaxInfo } from 'pages/helper/taxInfo';
import { useGetOnlinePaymentDiscountQuery } from 'core/hooks/useGetOnlinePaymentDiscountQuery';

const MAX_RETRY_COUNT = 3;

export const OnlinePaymentSummary = () => {
  const navigate = useNavigate();
  const { referenceNumber } = useParams<{ referenceNumber: string }>();
  const countryId = useAppSelector(selectSelectedCountryId);
  const [retryCount, setRetryCount] = useState(0);
  const [shouldShowError, setShouldShowError] = useState(false);

  const {
    data: candidateExamData,
    isFetching: candidateExamDataIsLoading,
    isError: candidateExamDataIsError,
  } = useGetCandidateExamDataQuery(referenceNumber!);
  const {
    data: paymentDetails,
    isFetching: paymentDetailsIsLoading,
    isError: paymentDetailsIsError,
    refetch: refetchPaymentDetails,
  } = useGetPaymentDetailsQuery(referenceNumber!);

  const { activeExams, preparationCentreName, sessionName } = usePaymentSummaryData(
    referenceNumber!,
    candidateExamData?.exams ?? [],
  );

  const { onlinePaymentDiscountAvailable } = useGetOnlinePaymentDiscountQuery();

  const discountAmount = useMemo(() => {
    if (!paymentDetails?.onlinePaymentFee || !paymentDetails?.offlinePaymentFee) {
      return 0;
    }
    return -(paymentDetails.offlinePaymentFee - paymentDetails.onlinePaymentFee);
  }, [paymentDetails]);

  const isLoading = useMemo(
    () => candidateExamDataIsLoading || paymentDetailsIsLoading,
    [candidateExamDataIsLoading, paymentDetailsIsLoading],
  );
  const isPaid = useMemo(() => paymentDetails && paymentDetails.isPaid && !isLoading, [paymentDetails, isLoading]);
  const isNotFound = useMemo(
    () => (paymentDetailsIsError || candidateExamDataIsError) && !isLoading,
    [paymentDetailsIsError, candidateExamDataIsError, isLoading],
  );
  const taxInfo = useMemo(() => {
    if (!paymentDetails) return '';
    return getTaxInfo(paymentDetails.taxRate, paymentDetails.fullFee);
  }, [paymentDetails]);

  useEffect(() => {
    if (!isLoading && paymentDetails && !paymentDetails.isPaid && retryCount < MAX_RETRY_COUNT && !shouldShowError) {
      const delays = [2000, 3000, 5000];
      const delay = delays[retryCount];

      const timeoutId = setTimeout(() => {
        refetchPaymentDetails();
        setRetryCount((prev) => prev + 1);
      }, delay);

      return () => clearTimeout(timeoutId);
    } else if (!isLoading && paymentDetails && !paymentDetails.isPaid && retryCount >= MAX_RETRY_COUNT) {
      setShouldShowError(true);
    }
  }, [isLoading, paymentDetails, retryCount, shouldShowError, refetchPaymentDetails]);

  return (
    <MainContainer>
      <h1>Online payment confirmation</h1>

      {(isLoading ||
        (!isLoading && !isPaid && retryCount >= 0 && retryCount < MAX_RETRY_COUNT && !shouldShowError)) && (
        <LoadingCard>
          {isLoading ? 'One second, please' : 'Waiting for payment information, please wait...'}
        </LoadingCard>
      )}

      {isPaid && (
        <>
          {countryId === CountryIdentifiers.BANGLADESH_ID && <BangladeshWarningMsg />}
          {countryId === CountryIdentifiers.UAE_ID && <UaeWarningMsg />}
          {countryId === CountryIdentifiers.KUWAIT_ID && <KuwaitWarningMsg />}
          <h2>What's next?</h2>
          <Quote title="Thank you for submitting your exams registration." intent="info">
            <p>Updates regarding your exams will be sent to you by email.</p>
          </Quote>
          <Divider />
          <ReadonlyField label="Registration type">{preparationCentreName ? 'School' : 'Private'}</ReadonlyField>
          <ReadonlyField label="Candidate UID">{candidateExamData?.candidateId}</ReadonlyField>
          <ReadonlyField label="Session">{sessionName}</ReadonlyField>
          <ReadonlyField label="Reference number">
            {paymentDetails?.paymentSlipNumber ?? paymentDetails?.reference}
          </ReadonlyField>
          {preparationCentreName && <ReadonlyField label="School name">{preparationCentreName}</ReadonlyField>}
          <Divider />
          <h2>You have been registered for the following exams:</h2>
          {activeExams.map((exam, i) => (
            <ExamCard key={i} exam={exam} />
          ))}

          {onlinePaymentDiscountAvailable && (
            <>
              <PriceLabel>
                Total fee:{' '}
                <strong>
                  {paymentDetails?.offlinePaymentFee
                    ? paymentDetails?.offlinePaymentFee.toFixed(2)
                    : paymentDetails?.fullFee.toFixed(2)}{' '}
                  {paymentDetails?.currency}
                </strong>
              </PriceLabel>
              <PriceLabel>
                Incentive applied:{' '}
                <strong>
                  {discountAmount.toFixed(2)} {paymentDetails?.currency}
                </strong>
              </PriceLabel>
              <PriceLabel>
                Total paid:{' '}
                <strong>
                  {paymentDetails?.fullFee.toFixed(2)} {paymentDetails?.currency}
                </strong>
                <br />
                {taxInfo}
              </PriceLabel>
            </>
          )}

          {!onlinePaymentDiscountAvailable && (
            <PriceLabel>
              Total price:{' '}
              <strong>
                {paymentDetails?.fullFee.toFixed(2)} {paymentDetails?.currency}
              </strong>
              <br />
              {taxInfo}
            </PriceLabel>
          )}

          <Button onClick={() => navigate(ROUTES.MY_REGISTRATIONS)}>View registrations summary</Button>
        </>
      )}

      {shouldShowError && <PaymentError {...{ referenceNumber: referenceNumber! }} />}

      {isNotFound && <PaymentNotFound />}
    </MainContainer>
  );
};

const PriceLabel = styled.p`
  text-align: right;
  margin-bottom: 0;
  margin: 30px 0;
`;
